import type { TransferRecipient } from '../../../project_starburst';

/**
 * Shape of a Service Desk `Coders` worker row. Every field is optional: rows arrive as
 * loosely-typed JSON blobs and a malformed one must not break the type-ahead.
 *
 * `id` maps from `worker_id`, NOT `user_id` — assignment is keyed off `worker_id`
 * (`service-desk`'s `handleReassignCases` resolves the assignee by `coder.data.worker_id`).
 */
interface WorkerRowFields {
  worker_id?: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  availability?: string;
  roles?: ReadonlyArray<{ name?: string }>;
  specialty?: string;
  queue_count?: number;
  case_count?: number;
}

/** Rows arrive either flat or wrapped in `{ data: ... }` depending on the query. */
const fieldsOf = (row: unknown): WorkerRowFields => {
  if (row == null || typeof row !== 'object') return {};
  const candidate = row as { data?: unknown };
  const inner =
    candidate.data != null && typeof candidate.data === 'object'
      ? candidate.data
      : row;
  return inner as WorkerRowFields;
};

const displayName = (f: WorkerRowFields): string => {
  const full = [f.first_name, f.last_name].filter(Boolean).join(' ').trim();
  return full.length > 0 ? full : (f.username ?? '').trim();
};

/** "Coder · Cardiology" — role first, then specialty when the row carries one. */
const roleLine = (f: WorkerRowFields): string | undefined => {
  const role = f.roles?.find((r) => Boolean(r?.name))?.name;
  const parts = [role, f.specialty].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

export function toTransferRecipients(rows: unknown): TransferRecipient[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map(fieldsOf)
    .filter((f) => Boolean(f.worker_id) && displayName(f).length > 0)
    .map((f) => {
      const caseCount = f.case_count ?? f.queue_count;
      return {
        id: String(f.worker_id),
        name: displayName(f),
        ...(roleLine(f) ? { role: roleLine(f) } : {}),
        ...(typeof caseCount === 'number' ? { caseCount } : {}),
        ...(f.worker_id && f.user_id && f.username && f.first_name && f.last_name
          ? { assignment: { worker_id: String(f.worker_id), user_id: String(f.user_id), username: f.username, first_name: f.first_name, last_name: f.last_name } }
          : {})
      };
    });
}

/**
 * Narrows an already-fetched worker list by the typed term: the backend `Coders` query takes no
 * term parameter, so the adapter fetches the tenant-scoped list and filters here.
 *
 * Availability is deliberately not a filter — the worklist's own reassignment UI hides
 * non-AVAILABLE coders, and matching that here would silently drop a valid recipient.
 */
export function narrowByTerm(
  recipients: readonly TransferRecipient[],
  term: string
): TransferRecipient[] {
  const needle = term.trim().toLowerCase();
  if (needle.length === 0) return [...recipients];
  return recipients.filter(
    (r) =>
      r.name.toLowerCase().includes(needle) ||
      (r.role ?? '').toLowerCase().includes(needle)
  );
}
